import { useEffect } from 'react';

const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
const GOOGLE_TRANSLATE_SCRIPT_SELECTOR = 'script[data-google-translate-script="true"]';
const GOOGLE_TRANSLATE_SCRIPT_SRC = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';

let googleTranslateLoaderPromise = null;

const loadGoogleTranslateScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  if (window.google?.translate?.TranslateElement) {
    return Promise.resolve();
  }

  if (googleTranslateLoaderPromise) {
    return googleTranslateLoaderPromise;
  }

  googleTranslateLoaderPromise = new Promise((resolve) => {
    const resolveWhenReady = () => {
      if (window.google?.translate?.TranslateElement) {
        resolve();
        return true;
      }
      return false;
    };

    window.googleTranslateElementInit = () => {
      resolveWhenReady();
    };

    const existingScript = document.querySelector(GOOGLE_TRANSLATE_SCRIPT_SELECTOR);
    if (existingScript) {
      if (resolveWhenReady()) return;
      existingScript.addEventListener('load', resolveWhenReady, { once: true });
    } else {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = GOOGLE_TRANSLATE_SCRIPT_SRC;
      script.setAttribute('data-google-translate-script', 'true');
      script.addEventListener('load', resolveWhenReady, { once: true });
      document.body.appendChild(script);
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (resolveWhenReady() || attempts >= 50) {
        window.clearInterval(timer);
        if (attempts >= 50) resolve();
      }
    }, 100);
  });

  return googleTranslateLoaderPromise;
};

const renderTranslateElement = (force = false) => {
  const mountNode = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);
  if (!mountNode) return false;
  if (!window.google?.translate?.TranslateElement) return false;
  if (mountNode.querySelector('.goog-te-combo')) return true;
  if (!force && mountNode.dataset.googleTranslateInitRequested === 'true') return false;

  try {
    mountNode.dataset.googleTranslateInitRequested = 'true';
    mountNode.innerHTML = '';
    new window.google.translate.TranslateElement({ pageLanguage: 'en' }, GOOGLE_TRANSLATE_ELEMENT_ID);
  } catch (error) {
    return false;
  }

  return Boolean(mountNode.querySelector('.goog-te-combo'));
};

const GTranslateBootstrap = () => {
  useEffect(() => {
    loadGoogleTranslateScript();
  }, []);

  return null;
};

const GTranslate = () => {
  useEffect(() => {
    let isMounted = true;
    let attempts = 0;
    let timer = null;
    let scriptReady = false;

    const ensureVisibleDropdown = () => {
      if (!isMounted) return;

      const shouldForceReinit = attempts > 0 && attempts % 20 === 0;
      const rendered = scriptReady ? renderTranslateElement(shouldForceReinit) : false;
      if (rendered) {
        const mountNode = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);
        if (mountNode) delete mountNode.dataset.googleTranslateInitRequested;
        if (timer) window.clearInterval(timer);
        return;
      }

      attempts += 1;
      if (attempts >= 80 && timer) {
        window.clearInterval(timer);
      }
    };

    loadGoogleTranslateScript().then(() => {
      if (!isMounted) return;
      scriptReady = true;
      ensureVisibleDropdown();
    });

    timer = window.setInterval(ensureVisibleDropdown, 150);

    return () => {
      isMounted = false;
      if (timer) window.clearInterval(timer);
    };
  }, []);

  return <div id={GOOGLE_TRANSLATE_ELEMENT_ID}></div>;
};

export { GTranslate, GTranslateBootstrap };
